import { useState } from "react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { Logo } from "../components/AppShell";
import { FreeGameNotice } from "../components/Regolamento";
import { Link } from "react-router-dom";

const HERO = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const Field = ({ id, label, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-xs font-mono uppercase tracking-widest text-slate-400">{label}</Label>
    <Input id={id} data-testid={`${id}-input`} className="h-12 bg-[#121824] border-[#26334D] focus-visible:ring-emerald-500" {...props} />
  </div>
);

const TITLES = {
  login: ["Bentornato", "Accedi per fare i tuoi pronostici."],
  register: ["Crea il tuo account", "Sfida gli amici a colpi di pronostici."],
  forgot: ["Password dimenticata", "Ti invieremo un codice via email."],
  reset: ["Nuova password", "Inserisci il codice ricevuto e la nuova password."],
};

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState(() => (new URLSearchParams(window.location.search).get("mode") === "register" ? "register" : "login"));
  const [f, setF] = useState({ name: "", email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(f.email, f.password);
      if (mode === "register") await register(f.name, f.email, f.password);
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: f.email });
        toast.success("Se l'email esiste, riceverai un codice");
        setMode("reset");
      }
      if (mode === "reset") {
        await api.post("/auth/reset-password", { email: f.email, code: f.code, password: f.password });
        toast.success("Password aggiornata, ora puoi accedere");
        setMode("login");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const [title, sub] = TITLES[mode];
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A0D14]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Stadio" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14] via-[#0A0D14]/60 to-[#0A0D14]/20" />
        <div className="absolute bottom-0 p-12 fade-up">
          <p className="font-mono text-xs uppercase tracking-widest text-emerald-400 mb-4">Il fantacalcio delle scommesse</p>
          <h1 className="font-display text-6xl font-black uppercase leading-[0.9] tracking-tight max-w-lg">
            Pronostica.<br />Sfida.<br /><span className="text-emerald-400">Vinci la lega.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-md">Tornei privati con gli amici, punti virtuali, classifiche aggiornate ogni giornata.</p>
        </div>
      </div>
      <div className="flex flex-col justify-center px-6 sm:px-16 py-12 pitch-bg">
        <div className="w-full max-w-md mx-auto fade-up">
          <Logo />
          <h2 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tight mt-12" data-testid="auth-title">{title}</h2>
          <p className="text-slate-400 mt-2 mb-8">{sub}</p>
          <form onSubmit={submit} className="space-y-5" data-testid={`auth-form-${mode}`}>
            {mode === "register" && <Field id="register-name" label="Nome" value={f.name} onChange={set("name")} required />}
            <Field id="auth-email" label="Email" type="email" value={f.email} onChange={set("email")} required />
            {mode === "reset" && <Field id="reset-code" label="Codice" value={f.code} onChange={set("code")} required />}
            {mode !== "forgot" && (
              <Field id="auth-password" label={mode === "reset" ? "Nuova password" : "Password"} type="password" value={f.password} onChange={set("password")} required minLength={mode === "login" ? 1 : 6} />
            )}
            {mode === "login" && (
              <button type="button" onClick={() => setMode("forgot")} className="text-sm text-slate-400 hover:text-emerald-400" data-testid="forgot-password-link">
                Password dimenticata?
              </button>
            )}
            <Button type="submit" disabled={busy} data-testid="auth-submit-button" className="w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold uppercase tracking-wider">
              {busy ? "Attendere…" : { login: "Accedi", register: "Registrati", forgot: "Invia codice", reset: "Aggiorna password" }[mode]}
            </Button>
          </form>
          <p className="mt-8 text-sm text-slate-400">
            {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-emerald-400 font-semibold hover:underline" data-testid="auth-toggle-mode">
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>
          <div className="mt-10 pt-6 border-t border-[#26334D] space-y-2">
            <FreeGameNotice />
            <div className="flex gap-5">
              <Link to="/regolamento" className="text-xs font-mono uppercase tracking-widest text-emerald-400 hover:underline" data-testid="login-rules-link">Leggi il regolamento</Link>
              <Link to="/privacy" className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-white" data-testid="login-privacy-link">Privacy</Link>
              <Link to="/termini" className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-white" data-testid="login-terms-link">Termini</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
